import React, { Suspense } from "react";
import { useLocation, Route, Switch } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setSidebarMini, selectSidebarMiniUserSet } from "store/reducers/uiReducer";
import Box from "@mui/material/Box";


// core components
// Our own logo, bundled rather than loaded from Builder.io's CDN.
// PNG, not WebP: Amplify's SPA rewrite has an extension allowlist that does
// not include webp, so the hosted .webp URL returned index.html and the
// wordmark rendered as broken alt text on every page. Same pixels.
// Brand refresh R5 (2026-09-04): the rev-2 wordmark — Hanken Grotesk outlines,
// one ink, teal tittles on all four i's. SVG outlines, so no font dependency
// and no Amplify rewrite-allowlist concern (svg is on the allowlist).
import optimicitiWordmark from "assets/img/brand/optimiciti_wordmark_refresh.svg";
import Navbar from 'components/common/Navbar.js';
import Footer from "components/common/Footer.js";
import Sidebar from "components/common/Sidebar.js";
import SkipToContent from "components/common/SkipToContent.jsx";
import SolveAnnouncer from "components/common/SolveAnnouncer.jsx";
import NotFound from "components/domain/NotFound.js";

import SolvePoller from "components/common/SolvePoller.jsx";

import routes from "routes.js";


const validLayouts = ['/home', '/admin', '/pricing', '/api', '/use-cases', '/careers',
  '/model-builder', '/terms-of-service', '/privacy-policy', '/checkout', '/account', '/industries'];

const Admin = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const sidebarMiniUserSet = useSelector(selectSidebarMiniUserSet);
  const mainContentRef = React.useRef(null);

  const isFirstRoute = React.useRef(true);
  React.useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    // SPA route changes swap content without a page load, so screen-reader and
    // keyboard users are left focused on the old page's link. Move focus to the
    // new page's <main> (skipped on first mount so initial focus stays natural).
    if (isFirstRoute.current) {
      isFirstRoute.current = false;
    } else {
      document.getElementById("main-content")?.focus({ preventScroll: true });
    }
    // Depend on the PATHNAME, not the whole location. A location object is new
    // whenever the query string changes too, and pages that mirror UI state into
    // the URL (the examples filter bar does this per keystroke via
    // history.replace) would otherwise fire this on every character typed —
    // yanking focus out of the search box and scrolling to top mid-word. A
    // changed query string is not a new page.
  }, [location.pathname]);

  React.useEffect(() => {
    // Default behavior (only if the user hasn't manually chosen open/closed):
    // - Home (/home): collapsed
    // - Other pages: expanded
    if (sidebarMiniUserSet) {
      return;
    }

    const marketingPaths = ["/home", "/pricing", "/api", "/use-cases", "/careers", "/terms-of-service", "/privacy-policy", "/industries"];
    const isMarketingPage = marketingPaths.some(p => location.pathname === p || location.pathname.startsWith(p + "/"));
    dispatch(setSidebarMini(isMarketingPage));
  }, [dispatch, location.pathname, sidebarMiniUserSet]);

  const getRoutes = (routes) => {
    return routes.map((prop, key) => {
      if (prop.collapse) {
        return getRoutes(prop.views);
      }
      if (validLayouts.includes(prop.layout)) {
        return (
          <Route
            path={prop.layout + prop.path}
            component={prop.component}
            key={key}
          />
        );
      } else {
        return null;
      }
    });
  };
  return (
    <>
      {/* Isolated leaf: hosts the async-solve poller's subscription so its
          frequent updates don't re-render this layout. */}
      <SolvePoller />
      <SolveAnnouncer />
      <SkipToContent />
      <Box display="flex" id='Admin-Box1' height='100vh'>
        {/* Desktop only — it hides itself below the navbar's mobile breakpoint. */}
        <Sidebar
          routes={routes}
          logo={{
            innerLink: "/home",
            imgSrc: optimicitiWordmark,
            imgAlt: "Optimiciti Logo",
          }}
        />
        <Box
          id='Admin-Box2'
          ref={mainContentRef}
          position="relative"
          flex="1"
          sx={{ backgroundColor: '#ffffff', minHeight: '100vh', width: '100%' }}
          style={{minWidth:'0', overflowX:'hidden', overflowY:'auto', height:'100%', display:'flex', flexDirection:'column'}}
        >
          <Navbar></Navbar>
          {/* The main landmark: skip-link target + route-change focus target.
              tabIndex={-1} makes it programmatically focusable without joining
              the tab order; the outline is suppressed since this focus is a
              navigation aid, not an interactive control. */}
          {/* overflow:visible is load-bearing, not cosmetic: argon.min.css carries a
              bare `main { overflow: hidden }` reset rule. It lay dormant while this
              wrapper was a <div>; making it a <main> landmark activated it, which
              clipped the content area so the page could not grow or scroll and the
              footer read as stuck. This restores the pre-landmark behavior (a plain
              div computes overflow:visible) and the sx class outranks the element
              selector, so it cannot be silently re-clipped. */}
          <Box component="main" id="main-content" tabIndex={-1} flex="1" sx={{ outline: "none", overflow: "visible" }}>
            {/* The optimizer, model builder, examples and account routes are code
                split (see routes.js), so rendering them needs a boundary. The
                fallback is deliberately empty rather than a spinner: these
                chunks resolve in milliseconds on a warm cache, and a flashed
                spinner reads as slower than nothing at all. */}
            <Suspense fallback={null}>
            <Switch>
              {getRoutes(routes)}
              <Route component={NotFound} />
            </Switch>
            </Suspense>
          </Box>
          <Footer />
        </Box>
      </Box>
    </>
  );
};

export default Admin;
